import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { useLocalStorage } from 'usehooks-ts';
import { Address } from 'viem';
import { useAccount, useDisconnect } from 'wagmi';
import { useAccountEquity } from '../../hooks/useAccountEquity';
import { useAccountMaintenanceMargin } from '../../hooks/useAccountMaintenanceMargin';
import { useAccountMargin } from '../../hooks/useAccountMargin';
import { useAccountUPnl } from '../../hooks/useAccountUPnl';
import { useModalState } from '../../hooks/useModalState';
import { usePositions } from '../../hooks/usePositions';
import { useProductPricesSub } from '../../hooks/useProductPricesSub';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { formatDollar, formatNumber, formatPercentage } from '../../lib/formatters';
import { Gwei } from '../../lib/gwei';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { truncateAddress } from '../../lib/stringHelpers';
import {
  accountEquityTooltip,
  accountLeverageTooltip,
  maintenanceMarginTooltip,
  marginUsageTooltip,
} from '../../lib/tooltips';
import { Button } from '../catalyst-ui-kit/button';
import { ClipboardButton } from '../ClipboardButton/ClipboardButton';
import { HealthIcon } from '../HealthIcon/HealthIcon';
import { getHealthColorClass } from '../HealthIcon/healthThreshold';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { Tooltip } from '../Tooltip/Tooltip';
import {
  useEffectiveLeverage,
  useMarginUsage,
  useTotalPositionValue,
} from '../TradePage/TradeForm/hooks/calculationHooks';
import { StatRow, StatRowValue } from '../TradePage/TradeForm/TradeFormStats';
import { WithdrawButton } from '../WithdrawButton/WithdrawButton';

type AccountModalUiProps = {
  open: boolean;
  onClose: () => void;
  walletAddress: Address | undefined;
  availableBalance: bigint | undefined;
  accountEquity: bigint | undefined;
  unrealisedPnl: bigint | undefined;
  maintenanceMargin: bigint | undefined;
  marginUsage: bigint | undefined;
  accountLeverage: bigint | undefined;
  hidePnl: boolean;
  onDeposit: () => void;
  onDisconnect: () => void;
  onWithdraw: () => Promise<void>;
};

export const AccountModalUi: React.FC<AccountModalUiProps> = ({
  open,
  onClose,
  walletAddress,
  availableBalance,
  accountEquity,
  unrealisedPnl,
  maintenanceMargin,
  marginUsage,
  accountLeverage,
  hidePnl,
  onDeposit,
  onDisconnect,
  onWithdraw,
}) => {
  const truncatedAddress = walletAddress ? truncateAddress(walletAddress) : '';

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={
        walletAddress ? (
          <div className="flex items-center gap-2">
            Account: <span className="text-sm text-contentSecondary">{truncatedAddress}</span>
            <ClipboardButton className="text-contentSecondary" text={walletAddress} iconSize={16} />
          </div>
        ) : (
          'Account'
        )
      }
    >
      <div className="flex flex-col gap-3">
        <StatRow label="Available Balance">
          <StatRowValue value={availableBalance} formatValue={formatDollar} />
        </StatRow>
        <StatRow label={<Tooltip content={accountEquityTooltip()}>Account Equity</Tooltip>}>
          {hidePnl ? '******' : <StatRowValue value={accountEquity} formatValue={formatDollar} />}
        </StatRow>
        <StatRow label="Unrealised P&L">
          {hidePnl ? (
            '******'
          ) : (
            <StatRowValue value={unrealisedPnl} formatValue={formatDollar} colorised />
          )}
        </StatRow>
        <StatRow label={<Tooltip content={maintenanceMarginTooltip()}>Maintenance Margin</Tooltip>}>
          <StatRowValue value={maintenanceMargin} formatValue={formatDollar} />
        </StatRow>
        <StatRow label={<Tooltip content={marginUsageTooltip()}>Margin Usage</Tooltip>}>
          {marginUsage !== undefined && <HealthIcon marginUsage={Gwei.fromGwei(marginUsage)} />}
          <StatRowValue
            value={marginUsage}
            formatValue={(x) => (x > Gwei.toGwei('1') ? '100%+' : formatPercentage(x))}
            className={
              marginUsage === undefined
                ? undefined
                : getHealthColorClass(Gwei.fromGwei(marginUsage))
            }
          />
        </StatRow>
        <StatRow label={<Tooltip content={accountLeverageTooltip()}>Account Leverage</Tooltip>}>
          <StatRowValue
            value={accountLeverage}
            formatValue={(x) =>
              `${formatNumber(x, { maximumFractionDigits: 1, minimumFractionDigits: 0 })}x`
            }
          />
        </StatRow>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <Button color="blue" className="w-full" onClick={onDeposit}>
          Deposit
        </Button>
        <div className="flex gap-3">
          <Button className="flex-1" onClick={onDisconnect}>
            <ArrowRightStartOnRectangleIcon />
            Disconnect
          </Button>
          <WithdrawButton className="flex-1" onBeforeOpenModal={onWithdraw} />
        </div>
      </div>
    </ResponsiveModal>
  );
};

export const AccountModal: React.FC = () => {
  const { address } = useAccount();
  const { disconnect } = useDisconnect();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: marginData } = useAccountMargin();
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const { data: pricesByProductId } = useProductPricesSub();
  const { data: maintenanceMargin } = useAccountMaintenanceMargin();
  const { data: accountUPnl } = useAccountUPnl();
  const accountEquity = useAccountEquity();
  const [hidePnl] = useLocalStorage(localStorageKeys.hidePnl, false);

  const accountModal = useModalState((state) => state.accountModal);
  const setAccountModal = useModalState((state) => state.setAccountModal);
  const setDepositModal = useModalState((state) => state.setDepositModal);

  const totalPositionValue = useTotalPositionValue(positions, pricesByProductId);

  const currentLeverage = useEffectiveLeverage({
    totalPositionValue,
    balance: marginData?.totalBalance,
  });

  const marginUsage = useMarginUsage({
    maintenanceMargin,
    subaccountTotalBalance: marginData?.totalBalance,
    accountUPnl: accountUPnl,
  });

  const handleClose = () => {
    setAccountModal('closed');
  };

  const handleDeposit = () => {
    handleClose();
    // Add delay to allow AccountModal to close before opening DepositModal
    setTimeout(() => {
      setDepositModal({ openOrClosed: 'open', context: 'Onboard' });
    }, 300);
  };

  const handleDisconnect = () => {
    handleClose();
    disconnect();
  };

  const handleWithdraw = async () => {
    handleClose();
    // Simulate a delay to show the modal
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 300);
    });
  };

  return (
    <AccountModalUi
      open={accountModal === 'open'}
      onClose={handleClose}
      walletAddress={address}
      availableBalance={marginData?.totalAvailable}
      accountEquity={accountEquity}
      unrealisedPnl={accountUPnl}
      maintenanceMargin={maintenanceMargin}
      marginUsage={marginUsage}
      accountLeverage={currentLeverage}
      hidePnl={hidePnl}
      onDeposit={handleDeposit}
      onDisconnect={handleDisconnect}
      onWithdraw={handleWithdraw}
    />
  );
};
